/**
 * InlineHelpBanner — a dismissible explanation block at the top of a page.
 *
 * Use on surfaces that pack more than one concept (workflows, webhooks,
 * analytics breakdowns, the page builder, etc.) so a new user gets a
 * one-paragraph orientation before they start clicking. Once they
 * dismiss it, it stays dismissed — keyed by the `id` prop in
 * localStorage, no backend round-trip.
 *
 * Usage:
 *
 *     <InlineHelpBanner
 *       id="workflows-intro"
 *       title="What is a workflow?"
 *     >
 *       A workflow runs automatically when something happens in LinkHub —
 *       e.g. "send an email when a QR is scanned in Cape Town". Pick a
 *       trigger, add one or more actions, and turn it on.
 *     </InlineHelpBanner>
 *
 * Notes:
 * - Pick a STABLE `id` per banner. Reusing one across two banners means
 *   dismissing one hides the other.
 * - SSR-safe — guards `localStorage` access so it doesn't blow up in
 *   environments without `window`.
 * - Renders nothing if dismissed; no flash on mount because we read
 *   localStorage synchronously in `useState`'s init lazy callback.
 */

import React, { useState } from 'react';
import { Info, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const STORAGE_PREFIX = 'linkhub:helpBanner:dismissed:';

const isDismissed = (id) => {
  if (typeof window === 'undefined') return false;
  try {
    return window.localStorage.getItem(STORAGE_PREFIX + id) === '1';
  } catch {
    return false;
  }
};

const persistDismissal = (id) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_PREFIX + id, '1');
  } catch {
    /* private mode etc. — silently no-op */
  }
};

export function InlineHelpBanner({
  id,
  title,
  children,
  className,
  icon: Icon = Info,
  dismissible = true,
}) {
  const [dismissed, setDismissed] = useState(() => isDismissed(id));

  if (dismissed) return null;

  const handleDismiss = () => {
    persistDismissal(id);
    setDismissed(true);
  };

  return (
    <div
      role="status"
      data-testid={`help-banner-${id}`}
      className={cn(
        'flex gap-3 rounded-lg border border-border bg-muted/40 p-4 text-sm',
        className
      )}
    >
      <div className="flex-shrink-0 pt-0.5">
        <Icon className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 space-y-1">
        {title && <p className="font-medium leading-tight">{title}</p>}
        <div className="text-muted-foreground leading-relaxed">{children}</div>
      </div>
      {dismissible && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDismiss}
          className="h-7 w-7 flex-shrink-0 text-muted-foreground hover:text-foreground"
          aria-label="Dismiss help"
          data-testid={`help-banner-dismiss-${id}`}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}

export default InlineHelpBanner;
